import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../config/api.config';
import type {
  CreatePaymentIntentPayload,
  PaymentIntentQuote,
  Transaction,
} from '../models/transaction.model';

@Injectable({ providedIn: 'root' })
export class TransactionsService {
  constructor(private readonly http: HttpClient) {}

  findMine(): Observable<Transaction[]> {
    return this.http.get<Transaction[]>(`${API_BASE_URL}/transactions/mine`);
  }

  findSelling(): Observable<Transaction[]> {
    return this.http.get<Transaction[]>(`${API_BASE_URL}/transactions/selling`);
  }

  /** Step 1 — get a fee quote and open a Stripe authorization hold. */
  createPaymentIntent(payload: CreatePaymentIntentPayload): Observable<PaymentIntentQuote> {
    return this.http.post<PaymentIntentQuote>(
      `${API_BASE_URL}/transactions/payment-intent`,
      payload,
    );
  }

  /** Step 2 — after Stripe Elements confirms the card, create the order. */
  confirm(paymentIntentId: string): Observable<Transaction> {
    return this.http.post<Transaction>(`${API_BASE_URL}/transactions/confirm`, { paymentIntentId });
  }

  accept(id: string): Observable<Transaction> {
    return this.http.post<Transaction>(`${API_BASE_URL}/transactions/${id}/accept`, {});
  }

  decline(id: string, reason?: string): Observable<Transaction> {
    return this.http.post<Transaction>(`${API_BASE_URL}/transactions/${id}/decline`, { reason });
  }

  /** Seller confirms the order shipped (with courier/tracking) or was
   * handed over — one half of the two-party escrow confirmation.
   * courier/trackingNumber are only used for DELIVERY orders. Only starts
   * the inspection window once the buyer has also confirmed via confirmReceipt(). */
  confirmFulfillment(
    id: string,
    payload: { courier?: string; trackingNumber?: string },
  ): Observable<Transaction> {
    return this.http.post<Transaction>(
      `${API_BASE_URL}/transactions/${id}/confirm-fulfillment`,
      payload,
    );
  }

  /** Buyer confirms they received the item — the other half of the
   * two-party escrow confirmation. */
  confirmReceipt(id: string): Observable<Transaction> {
    return this.http.post<Transaction>(`${API_BASE_URL}/transactions/${id}/confirm-receipt`, {});
  }

  requestRefund(id: string, reason?: string): Observable<Transaction> {
    return this.http.post<Transaction>(`${API_BASE_URL}/transactions/${id}/request-refund`, {
      reason,
    });
  }

  refund(id: string): Observable<Transaction> {
    return this.http.post<Transaction>(`${API_BASE_URL}/transactions/${id}/refund`, {});
  }

  complete(id: string): Observable<Transaction> {
    return this.http.post<Transaction>(`${API_BASE_URL}/transactions/${id}/complete`, {});
  }

  cancel(id: string): Observable<Transaction> {
    return this.http.post<Transaction>(`${API_BASE_URL}/transactions/${id}/cancel`, {});
  }

  review(id: string, rating: number, body: string, title?: string): Observable<unknown> {
    return this.http.post(`${API_BASE_URL}/transactions/${id}/review`, { rating, body, title });
  }
}
